import { getMessaging, getToken, onMessage, MessagePayload, isSupported } from 'firebase/messaging'
import { subscribeToFCMTopic, unsubscribeFromFCMTopic, sendFCMTestNotification } from './mock-api'

// Initialize Firebase Cloud Messaging (only in browser context)
let messaging: ReturnType<typeof getMessaging> | null = null

// Helper function to get messaging instance
async function getMessagingSafe(): Promise<ReturnType<typeof getMessaging> | null> {
  if (typeof window === 'undefined') {
    return null
  }

  // Import app dynamically to avoid initialization issues
  const appModule = await import('@/lib/firebase')
  const appInstance = appModule.default

  if (!appInstance) {
    console.warn('Firebase app is not initialized')
    return null
  }

  try {
    const supported = await isSupported()
    if (!supported) {
      console.warn('Firebase Messaging is not supported in this browser')
      return null
    }

    if (!messaging) {
      messaging = getMessaging(appInstance)
    }
    return messaging
  } catch (error) {
    console.warn('Firebase Messaging initialization failed:', error)
    return null
  }
}

export interface NotificationPayload extends MessagePayload {
  data?: {
    signalId?: string
    symbol?: string
    side?: string
    entry?: string
    action?: string
    url?: string
  }
}

export interface TopicSubscription {
  topic: string
  name: string
  description: string
  enabled: boolean
}

export const AVAILABLE_TOPICS: TopicSubscription[] = [
  {
    topic: 'pulse.signals',
    name: 'All Signals',
    description: 'Receive notifications for all trading signals',
    enabled: true
  },
  {
    topic: 'pulse.US30',
    name: 'US30 Signals',
    description: 'Signals for US30 index trading',
    enabled: false
  },
  {
    topic: 'pulse.BTCUSDT',
    name: 'Bitcoin Signals',
    description: 'Signals for Bitcoin trading',
    enabled: false
  },
  {
    topic: 'pulse.ETHUSDT',
    name: 'Ethereum Signals',
    description: 'Signals for Ethereum trading',
    enabled: false
  },
  {
    topic: 'pulse.LondonBreakout',
    name: 'London Breakout',
    description: 'Signals for London session breakout strategy',
    enabled: false
  },
  {
    topic: 'pulse.Scalping',
    name: 'Scalping Strategy',
    description: 'High-frequency scalping signals',
    enabled: false
  }
]

export class NotificationService {
  private static instance: NotificationService
  private vapidKey: string

  constructor() {
    this.vapidKey = import.meta.env.VITE_FCM_VAPID_KEY || ''
  }

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService()
    }
    return NotificationService.instance
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      throw new Error('This browser does not support notifications')
    }

    if (Notification.permission === 'granted') {
      return 'granted'
    }

    if (Notification.permission === 'denied') {
      throw new Error('Notification permission denied')
    }

    const permission = await Notification.requestPermission()
    return permission
  }

  async getToken(): Promise<string | null> {
    try {
      const messagingInstance = await getMessagingSafe()
      if (!messagingInstance) {
        console.warn('Firebase Messaging not initialized')
        return null
      }

      if (!this.vapidKey) {
        console.warn('VAPID key not configured')
        return null
      }

      const token = await getToken(messagingInstance, {
        vapidKey: this.vapidKey
      })

      return token
    } catch (error) {
      console.error('Error getting FCM token:', error)
      return null
    }
  }

  async subscribeToTopic(topic: string): Promise<boolean> {
    try {
      const token = await this.getToken()
      if (!token) {
        throw new Error('No FCM token available')
      }

      const result = await subscribeToFCMTopic({ token, topic })
      
      if (!result.success) {
        throw new Error(result.message)
      }

      console.log(`Subscribed to topic: ${topic}`)
      return true
    } catch (error) {
      console.error('Error subscribing to topic:', error)
      return false
    }
  }

  async unsubscribeFromTopic(topic: string): Promise<boolean> {
    try {
      const token = await this.getToken()
      if (!token) {
        throw new Error('No FCM token available')
      }

      const result = await unsubscribeFromFCMTopic({ token, topic })
      
      if (!result.success) {
        throw new Error(result.message)
      }

      console.log(`Unsubscribed from topic: ${topic}`)
      return true
    } catch (error) {
      console.error('Error unsubscribing from topic:', error)
      return false
    }
  }

  setupMessageListener(onMessageReceived: (payload: NotificationPayload) => void): void {
    getMessagingSafe().then((messagingInstance) => {
      if (!messagingInstance) {
        console.warn('Firebase Messaging not initialized, cannot setup message listener')
        return
      }
      
      onMessage(messagingInstance, (payload) => {
        console.log('Message received:', payload)
        onMessageReceived(payload as NotificationPayload)
      })
    }).catch((error) => {
      console.error('Error setting up message listener:', error)
    })
  }

  async sendTestNotification(): Promise<void> {
    try {
      const token = await this.getToken()
      if (!token) {
        throw new Error('No FCM token available')
      }

      const result = await sendFCMTestNotification({
        token,
        title: 'Test Notification',
        body: 'This is a test notification from Risko Pulse',
        data: {
          action: 'test'
        }
      })

      if (!result.success) {
        throw new Error(result.message)
      }

      console.log('Test notification sent')
    } catch (error) {
      console.error('Error sending test notification:', error)
    }
  }

  handleNotificationClick(payload: NotificationPayload): void {
    if (payload.data?.signalId) {
      // Navigate to specific signal
      window.location.href = `/dashboard?signal=${payload.data.signalId}`
    } else if (payload.data?.url) {
      // Navigate to custom URL
      window.location.href = payload.data.url
    } else {
      // Navigate to dashboard
      window.location.href = '/dashboard'
    }
  }
}

export const notificationService = NotificationService.getInstance()
